import { createSlice } from "@reduxjs/toolkit";
import { formatISO } from "date-fns";

import {
  assignFeed,
  createFeedEditorial,
  fetchCategoryFilters,
  fetchFeedAssignmentId,
  fetchFeedAssignments,
  fetchFeedDetails,
  fetchFeedEditorial,
  fetchFeedInternalNotesFilters,
  updateFeedEditorial,
  bulkUploadDocument,
} from "../../actions/feed/feedData";

const initialState = {
  // API states - Common filters
  feedFilters: {},
  feedFiltersLoading: true,
  feedLearningFilters: {
    category: [],
    learningPoint: [],
    categoryCount: 0,
    learningPointCount: 0,
  },
  feedLearningFiltersCategoryPage: 1,
  feedLearningFiltersCategoryPageSize: 20,
  // API states - Feed editorials
  feedList: [],
  feedAssignmentDetail: null,
  feedListCount: 0,
  feedListPage: 1,
  feedListPageSize: 10,
  feedListLoading: true,
  feedListSelector: null,
  feedDocumentSelector: null,
  feedDetail: {},
  feedDetailLoading: true,
  feedDeleting: false,
  feedDuplicating: false,
  feedCreating: false,
  // Table filters
  contentTitleFilter: "",
  selectedOrgFilter: [],
  selectedOrgFilterChange: false,
  selectedContentClinicFilter: [],
  selectedRoleFilter: [],
  selectedContentTypeFilter: "All",
  selectedAssignmentStatusFilter: "All",
  selectedContentTypeFilterChange: false,
  selectedUserTypeFilter: [],
  selectedGroupsFilter: [],
  selectedUserTypeFilterChange: [],
  selectedUserFilter: [],
  feedInternalNotesList: [],
  feedInternalNotesListCount: 0,
  feedInternalNotesListPage: 1,
  feedInternalNotesLoading: true,
  uploadfeedMediaLink: null,
  selectedStatusFilter: "",
  selectedInternalNotesFilter: [],
  selectedSubDxFilter: null,
  selectedCategory: [],
  selectedEndDate: null,
  selectedStartDate: null,
  // Page level data states
  feedArticlesErrValidate: false,
  feedCreateImagePreview: null,
  feedCreateVideoPreview: null,
  feedNewArticleTitle: "",

  feedNewArticleDescription: "",
  feedNewArticlePublication: "",
  uploadedDocumentSize: null,
  uploadedDocumentType: "",
  feedNewArticleCategory: [],
  feedNewArticlePrimaryTags: [],
  feedNewArticleSecondaryTags: [],
  feedNewArticleUserType: [],
  feedNewArticleUserRole: [],
  feedNewArticleContentType: "FREE",
  feedNewArticleStartDate: null,
  feedNewArticleEndDate: null,
  feedNewArticleOrgs: [],
  feedNewArticleAllOrgAccess: true,
  feedNewArticleAllUserRoles: true,
  feedNewArticleAllUserTypes: true,
  feedNewArticleCreating: false,
  feedAssignedCreating: false,
  buldkDocumentUploading: false,
  feedEditUploadProgress: 0,
  // ui states
  feedCreateSkeletonData: [
    {
      height: 40,
      marginBottom: "10px",
    },
    {
      height: 100,
      marginBottom: "8px",
    },
    {
      height: 100,
      marginBottom: "8px",
    },
    {
      height: 100,
      marginBottom: "8px",
    },
    {
      height: 100,
      marginBottom: "8px",
    },
  ],
};

export const feedSlice = createSlice({
  name: "feed",
  initialState,
  reducers: {
    // Common update
    setFeedSliceData: (state, { payload }) => {
      if (payload.type && payload.type in state) {
        state[payload.type] = payload.data;
      }
    },
    pushFeedSliceData: (state, { payload }) => {
      if (payload.type && payload.type in state) {
        state[payload.type].push(payload.data);
      }
    },
    popFeedSliceData: (state, { payload }) => {
      if (payload.type && payload.type in state) {
        if (state[payload.type].includes(payload.data)) {
          // remove from array of question ids
          state[payload.type].splice(
            state[payload.type].indexOf(payload.data),
            1
          );
        }
      }
    },
    toggleFeedSliceArrayData: (state, { payload }) => {
      if (payload.type && payload.type in state) {
        if (state[payload.type].includes(payload.data)) {
          // remove from array of question ids
          state[payload.type].splice(
            state[payload.type].indexOf(payload.data),
            1
          );
        } else {
          state[payload.type].push(payload.data);
        }
      }
    },
    // Module state update
    resetFeedCreateData: (state, { type }) => {
      state.feedCreateImagePreview = null;
      state.feedCreateVideoPreview = null;
      state.feedArticlesErrValidate = false;
      state.feedNewArticleTitle = "";
      state.feedNewArticleDescription = "";
      state.feedNewArticlePublication = "";
      state.feedNewArticleInternalNotes = "";
      state.feedNewArticleCategory = [];
      state.feedNewArticlePrimaryTags = [];
      state.feedNewArticleSecondaryTags = [];
      state.feedNewArticleUserType = [];
      state.feedNewArticleUserRole = [];
      state.feedNewArticleOrgs = [];
      state.feedNewArticleStartDate = null;
      state.feedNewArticleEndDate = null;
      state.feedNewArticleAllOrgAccess = true;
      state.feedNewArticleAllUserRoles = true;
      state.feedNewArticleAllUserTypes = true;
      localStorage.setItem("feedUploadProgress", 0);
    },
  },
  extraReducers: {
    // Feed articles list
    [fetchFeedEditorial.pending]: (state) => {
      state.feedListLoading = true;
    },
    [fetchFeedEditorial.fulfilled]: (state, { payload }) => {
      state.feedList = payload?.results;
      state.feedListCount = payload?.count ? payload.count : 0;
      state.feedListLoading = false;
    },

    [fetchFeedEditorial.rejected]: (state, {}) => {
      state.feedListLoading = false;
    },
    [fetchFeedAssignments.pending]: (state) => {
      state.feedListLoading = true;
    },
    [fetchFeedAssignments.fulfilled]: (state, { payload }) => {
      state.feedList = payload?.results;
      state.feedListCount = payload?.count ? payload.count : 0;
      state.feedListLoading = false;
    },
    [fetchFeedAssignments.rejected]: (state, { payload }) => {
      state.feedListLoading = false;
    },
    //Detail
    [fetchFeedAssignmentId.pending]: (state) => {
      state.feedListLoading = true;
    },
    [fetchFeedAssignmentId.fulfilled]: (state, { payload }) => {
      state.feedAssignmentDetail = payload;
      state.feedListLoading = false;
    },
    [fetchFeedAssignmentId.rejected]: (state, { payload }) => {
      state.feedListLoading = false;
    },
    // Feed Category filters
    [fetchCategoryFilters.pending]: (state) => {
      state.feedFiltersLoading = true;
    },
    [fetchCategoryFilters.fulfilled]: (state, { payload }) => {
      state.feedLearningFilters.category =
        payload?.results &&
        state.feedLearningFilters.category.concat(payload.results);
      state.feedLearningFilters.categoryCount = payload?.count
        ? payload.count
        : 0;
      state.feedFiltersLoading = false;
    },
    [fetchCategoryFilters.rejected]: (state, { payload }) => {
      state.feedFiltersLoading = false;
    },
    // Feed Details
    [fetchFeedDetails.pending]: (state) => {
      state.feedDetailLoading = true;
    },
    [fetchFeedDetails.fulfilled]: (state, { payload }) => {
      state.feedDetailLoading = false;
      if (payload?.post_id) {
        state.feedCreateVideoPreview = payload?.video ? payload.video : null;
        state.feedCreateImagePreview =
          payload?.post_type === "VIDEO" ? payload.thumbnail : payload.image;

        state.feedNewArticleTitle = payload?.title ? payload.title : "";
        state.uploadedDocumentSize =
          payload?.post_type === "DOCUMENT" ? payload?.size : null;
        (state.uploadfeedMediaLink =
          payload?.post_type === "DOCUMENT" ? payload?.media : null),
          (state.uploadedDocumentType =
            payload?.post_type === "DOCUMENT" ? payload?.file_type : "");
        state.feedNewArticleDescription = payload?.description
          ? payload.description
          : "";
        state.feedNewArticlePublication = payload?.publication
          ? payload.publication
          : "";
        state.feedNewArticleCategory = payload?.categories
          ? payload.categories
          : [];
        state.feedNewArticlePrimaryTags = payload?.primary_tags
          ? payload.primary_tags
          : [];
        state.feedNewArticleSecondaryTags = payload?.secondary_tags
          ? payload.secondary_tags
          : [];
        state.feedNewArticleUserType = payload?.user_type
          ? payload.user_type
          : [];
        state.feedNewArticleContentType = payload?.content_type
          ? payload.content_type
          : "";
        state.feedNewArticleInternalNotes = payload?.internal_notes
          ? payload.internal_notes
          : "";
        state.feedNewArticleUserRole = payload?.user_role
          ? payload.user_role
          : [];
        state.feedNewArticleStartDate = payload?.start_date
          ? formatISO(new Date(payload.start_date))
          : null;
        state.feedNewArticleEndDate = payload?.end_date
          ? formatISO(new Date(payload.end_date))
          : null;
        state.feedNewArticleOrgs = payload?.organization
          ? payload.organization
          : [];
        state.feedNewArticleAllOrgAccess =
          payload && "all_org_access" in payload
            ? payload.all_org_access
            : true;
        state.feedNewArticleAllUserRoles =
          payload && "all_user_role_access" in payload
            ? payload.all_user_role_access
            : true;
        state.feedNewArticleAllUserTypes =
          payload && "all_user_type_access" in payload
            ? payload.all_user_type_access
            : true;
      }
    },
    [fetchFeedDetails.rejected]: (state, { payload }) => {
      state.feedDetailLoading = false;
    },
    // Feed Create
    [createFeedEditorial.pending]: (state) => {
      state.feedNewArticleCreating = true;
    },
    [createFeedEditorial.fulfilled]: (state, { payload }) => {
      state.feedNewArticleCreating = false;
    },
    [createFeedEditorial.rejected]: (state, { payload }) => {
      state.feedNewArticleCreating = false;
    },

    [fetchFeedDetails.rejected]: (state, { payload }) => {
      state.feedDetailLoading = false;
    },
    // Assign feed Create
    [assignFeed.pending]: (state) => {
      state.feedAssignedCreating = true;
    },
    [assignFeed.fulfilled]: (state, { payload }) => {
      state.feedAssignedCreating = false;
    },
    [assignFeed.rejected]: (state, { payload }) => {
      state.feedAssignedCreating = false;
    },
    //document upload
    [bulkUploadDocument.pending]: (state) => {
      state.buldkDocumentUploading = true;
    },
    [bulkUploadDocument.fulfilled]: (state, { payload }) => {
      state.buldkDocumentUploading = false;
    },
    [bulkUploadDocument.rejected]: (state, { payload }) => {
      state.buldkDocumentUploading = false;
    },

    // Feed Update
    [updateFeedEditorial.pending]: (state) => {
      state.feedNewArticleCreating = true;
    },
    [updateFeedEditorial.fulfilled]: (state, { payload }) => {
      state.feedNewArticleCreating = false;
    },
    [updateFeedEditorial.rejected]: (state, { payload }) => {
      state.feedNewArticleCreating = false;
    },
    [fetchFeedInternalNotesFilters.pending]: (state) => {
      state.feedInternalNotesLoading = true;
    },
    [fetchFeedInternalNotesFilters.fulfilled]: (state, { payload }) => {
      state.feedInternalNotesList =
        payload.results && payload.results.length > 0
          ? payload.results
              .map((type) => type.internal_notes)
              .filter((type) => !!type)
          : [];
      state.feedInternalNotesListCount = payload?.count ? payload.count : 0;
      state.feedInternalNotesLoading = false;
    },
    [fetchFeedInternalNotesFilters.rejected]: (state, { payload }) => {
      state.feedInternalNotesLoading = false;
    },
  },
});

export const {
  setFeedSliceData,
  toggleFeedSliceArrayData,
  resetFeedCreateData,
  pushFeedSliceData,
  popFeedSliceData,
} = feedSlice.actions;

export default feedSlice.reducer;
